<template>
  <!--
    Habitaciones de la reserva.

    NO dibuja tarjeta: el padre (PageReservation) ya envuelve este bloque en .rsv-card.
    Antes pintaba su propia tarjeta dentro, y cada fila una caja rellena encima → tres
    niveles anidados. Ahora es el CUERPO de la tarjeta del padre y las filas se separan
    con una línea fina, que es lo que hacen el resto de listas del panel.
  -->
  <section v-if="mostrar" class="grp">
    <header class="grp-head">
      <div class="grp-head__title">
        <span class="grp-icon"><i class="fa-solid fa-layer-group"></i></span>
        <div class="grp-head__text">
          <h3 class="grp-h3">{{ $t('reservasPanels.groupRooms.heading') }}</h3>
          <p class="grp-sub">
            <template v-if="esCombinada">{{ $tc('reservasPanels.groupRooms.typesCount', lines.length, { count: lines.length }) }} · </template>
            {{ $tc('reservasPanels.groupRooms.roomsCount', groupRooms, { count: groupRooms }) }} ·
            {{ $tc('reservasPanels.groupRooms.guestsCount', groupGuests, { count: groupGuests }) }}
          </p>
        </div>
      </div>
      <div class="grp-total">
        <span class="grp-total__label">{{ $t('reservasPanels.groupRooms.totalLabel') }}</span>
        <span class="grp-total__value">{{ money(groupTotal) }}</span>
      </div>
    </header>

    <ul class="grp-list">
      <li
        v-for="line in lines"
        :key="line.reserva_id"
        class="grp-line"
        :class="{ 'is-current': Number(line.reserva_id) === Number(currentReservaId) }"
      >
        <!-- Columna 1 · qué es: tipo, ocupación, tarifa y habitaciones ocupadas.
             En tres alturas de lectura en vez de una ristra de puntos: el tipo se lee
             solo, la tarifa se reconoce por color y las habitaciones tienen su etiqueta. -->
        <div class="grp-line__info">
          <div class="grp-line__name">
            <span class="grp-line__unit">{{ line.unit_name }}</span>
            <span v-if="Number(line.reserva_id) === Number(currentReservaId)" class="grp-badge-current">{{ $t('reservasPanels.groupRooms.viewingBadge') }}</span>
          </div>

          <div class="grp-line__meta">
            <span>{{ $tc('reservasPanels.groupRooms.guestsCount', line.guests, { count: line.guests }) }}</span>
            <!-- Tarifa: dos significados, dos colores. Propia (morado de marca) o
                 vendida por un canal (azul), para no tener que leer el texto. -->
            <template v-if="line.rate_plan_name">
              <span class="grp-dot" aria-hidden="true">·</span>
              <span class="grp-rate" :class="{ 'grp-rate--ota': line.is_ota }">
                <i v-if="line.is_ota" class="fa-solid fa-link grp-rate__icon" aria-hidden="true"></i>
                {{ line.rate_plan_name }}
              </span>
            </template>
          </div>

          <!-- Habitaciones físicas. El NÚMERO va en negro y en negrita porque es lo que
               el anfitrión canta por la radio («la 24»); el nombre lo acompaña en gris y
               se omite cuando repite el del tipo, que es lo que hacía que se leyera
               «Habitación Doble · Habitación Doble». -->
          <div class="grp-rooms" v-if="habitaciones(line).length">
            <i class="fa-solid fa-door-closed grp-rooms__icon" aria-hidden="true"></i>
            <span
              v-for="hab in habitaciones(line)"
              :key="hab.key"
              class="grp-room"
              :class="{ 'grp-room--solo': !hab.name }"
            >
              <b v-if="hab.code" class="grp-room__code">{{ hab.code }}</b>
              <span v-if="hab.name" class="grp-room__name">{{ hab.name }}</span>
            </span>
          </div>
          <p v-else class="grp-rooms grp-rooms--none">
            <i class="fa-regular fa-circle-question grp-rooms__icon" aria-hidden="true"></i>
            {{ $t('reservasPanels.groupRooms.noRoomAssigned') }}
          </p>
        </div>

        <!-- Columna 2 · cuántas. En una línea de OTA la cantidad la manda el canal, así
             que se desactiva con el motivo en el título en vez de dejar que el guardado
             devuelva un error. -->
        <div class="grp-line__qty">
          <button
            class="grp-step"
            :disabled="busy || line.is_ota || line.rooms <= 1"
            :title="line.is_ota ? tituloOta(line) : $t('reservasPanels.groupRooms.removeOneRoom')"
            :aria-label="line.is_ota ? tituloOta(line) : $t('reservasPanels.groupRooms.removeOneRoom')"
            @click="changeQty(line, line.rooms - 1)"
          >−</button>
          <span class="grp-step__n">{{ line.rooms }}</span>
          <button
            class="grp-step"
            :disabled="busy || line.is_ota"
            :title="line.is_ota ? tituloOta(line) : $t('reservasPanels.groupRooms.addOneRoom')"
            :aria-label="line.is_ota ? tituloOta(line) : $t('reservasPanels.groupRooms.addOneRoom')"
            @click="changeQty(line, line.rooms + 1)"
          >+</button>
        </div>

        <!-- Columna 3 · cuánto -->
        <div class="grp-line__price">{{ money(line.total_to_pay != null ? line.total_to_pay : line.price) }}</div>

        <div class="grp-line__actions">
          <template v-if="confirmingId === line.reserva_id">
            <span class="grp-confirm-txt">{{ $t('reservasPanels.groupRooms.confirmRemove') }}</span>
            <button class="grp-mini grp-mini--danger" :disabled="busy" @click="removeLine(line)">{{ $t('reservasPanels.groupRooms.yes') }}</button>
            <button class="grp-mini" :disabled="busy" @click="confirmingId = null">{{ $t('reservasPanels.groupRooms.no') }}</button>
          </template>
          <button
            v-else
            class="grp-remove"
            :disabled="busy || line.is_ota || lines.length <= 1"
            :title="line.is_ota ? tituloOta(line) : (lines.length <= 1 ? $t('reservasPanels.groupRooms.cannotRemoveOnly') : $t('reservasPanels.groupRooms.removeRoomFromBooking'))"
            :aria-label="line.is_ota ? tituloOta(line) : $t('reservasPanels.groupRooms.removeRoomFromBooking')"
            @click="confirmingId = line.reserva_id"
          >
            <i class="fa-solid fa-trash-can" aria-hidden="true"></i>
          </button>
        </div>
      </li>
    </ul>

    <p v-if="errorMsg" class="grp-error">{{ errorMsg }}</p>
    <div v-if="busy" class="grp-busy"><i class="fa-solid fa-spinner fa-spin"></i> {{ $t('reservasPanels.groupRooms.updating') }}</div>
  </section>
</template>

<script>
import { formatCurrencyCode } from "@/utils/formatters";
import axios from "axios";

export default {
  name: "GroupRoomsPanel",
  props: {
    reservationData: { type: Object, required: true },
    currentReservaId: { type: [Number, String], default: null },
  },
  data() {
    return { busy: false, confirmingId: null, errorMsg: "" };
  },
  computed: {
    isGroup() {
      return Boolean(this.reservationData && this.reservationData.is_group);
    },
    /**
     * El panel se muestra SIEMPRE que haya líneas, no solo en las reservas combinadas.
     *
     * Antes iba con `isGroup` (varias filas con el mismo código), así que una reserva
     * creada a mano con 3 habitaciones del MISMO tipo decía «3 habitaciones» y no
     * enseñaba CUÁLES, aunque estuvieran asignadas en la base (17216JMPKQ ocupaba la
     * 209, la 306 y la 307). El anfitrión necesita saber qué habitaciones ocupa una
     * reserva, la venga por donde venga.
     */
    mostrar() {
      return this.lines.length > 0;
    },
    /** «Combinada» solo si de verdad hay varios TIPOS de habitación. */
    esCombinada() {
      return this.isGroup && this.lines.length > 1;
    },
    lines() {
      const l = this.reservationData && this.reservationData.group_lines;
      return Array.isArray(l) ? l : [];
    },
    groupTotal() {
      return Number((this.reservationData && this.reservationData.group_total) || 0);
    },
    groupRooms() {
      return Number((this.reservationData && this.reservationData.group_rooms) || 0);
    },
    groupGuests() {
      return Number((this.reservationData && this.reservationData.group_guests) || 0);
    },
    currency() {
      return (
        (this.reservationData && this.reservationData.propiedad && this.reservationData.propiedad.currency) ||
        (this.reservationData && this.reservationData.currency) ||
        "EUR"
      );
    },
  },
  methods: {
    /**
     * Habitaciones físicas de una línea, listas para pintar: { code, name }.
     *
     * El nombre se OMITE cuando repite el del tipo de habitación. Cada anfitrión
     * rellena los campos a su manera y hay hoteles donde la habitación 24 se llama
     * literalmente «Habitación Doble», igual que su tipo, así que sin esto la ficha
     * decía «Habitación Doble» justo debajo de «Habitación Doble».
     *
     * Si el backend aún no manda `rooms_detail` (versión anterior), se cae a
     * `room_codes`, que ya viene con el número y el nombre unidos por un guion.
     */
    habitaciones(line) {
      const tipo = String(line.unit_name || "").trim().toLowerCase();

      if (Array.isArray(line.rooms_detail) && line.rooms_detail.length) {
        return line.rooms_detail.map((h, i) => {
          const code = h.code ? String(h.code).trim() : "";
          const name = h.name ? String(h.name).trim() : "";
          const nombreRedundante = !name || name.toLowerCase() === tipo || name === code;
          return {
            key: h.id || `${code}-${name}-${i}`,
            code: code || null,
            // Sin número, el nombre ES el identificador y se pinta como tal.
            name: code ? (nombreRedundante ? null : name) : null,
            ...(code ? {} : { code: name || null }),
          };
        });
      }

      return (line.room_codes || []).map((c, i) => ({ key: `${c}-${i}`, code: String(c), name: null }));
    },
    /** Por qué no se puede tocar una línea que viene de un canal. */
    tituloOta(line) {
      const canal = line && line.channel_name ? line.channel_name : this.$t("reservasPanels.groupRooms.theChannel");
      return this.$t("reservasPanels.groupRooms.otaManaged", { channel: canal });
    },
    /* El CÓDIGO en letras, como el resto de la ficha. Antes era `style: "currency"`
       con el idioma del NAVEGADOR (`undefined`), así que el mismo importe se escribía
       distinto según quién mirase, y con símbolo: £ donde debía poner EUR. */
    money(v) {
      return formatCurrencyCode(v, this.currency);
    },
    notify(severity, detail) {
      if (this.$toast && this.$toast.add) {
        this.$toast.add({ severity, summary: severity === "error" ? this.$t("reservasPanels.groupRooms.notifyError") : this.$t("reservasPanels.groupRooms.notifyDone"), detail, life: 3500 });
      }
    },
    async changeQty(line, qty) {
      if (this.busy || qty < 1 || qty === line.rooms) return;
      this.errorMsg = "";
      this.busy = true;
      try {
        await axios.post("/booking-group/set-line-quantity", { reserva_id: line.reserva_id, quantity: qty });
        this.notify("success", this.$t("reservasPanels.groupRooms.qtyUpdated"));
        this.$emit("changed");
      } catch (e) {
        this.errorMsg = this.errMsg(e);
        this.notify("error", this.errorMsg);
      } finally {
        this.busy = false;
      }
    },
    async removeLine(line) {
      if (this.busy) return;
      this.errorMsg = "";
      this.busy = true;
      try {
        await axios.post("/booking-group/remove-line", { reserva_id: line.reserva_id });
        this.notify("success", this.$t("reservasPanels.groupRooms.roomRemoved"));
        this.confirmingId = null;
        // Si se quitó la línea que se está viendo, indicar a qué otra línea saltar.
        const fallback = this.lines.find((l) => Number(l.reserva_id) !== Number(line.reserva_id));
        this.$emit("changed", {
          removedId: line.reserva_id,
          fallbackId: fallback ? fallback.reserva_id : null,
        });
      } catch (e) {
        this.errorMsg = this.errMsg(e);
        this.notify("error", this.errorMsg);
      } finally {
        this.busy = false;
      }
    },
    errMsg(e) {
      return (
        (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
        this.$t("reservasPanels.groupRooms.updateError")
      );
    },
  },
};
</script>

<style scoped>
/*
  Todo el color y el espaciado sale de los tokens del detalle de reserva (--rsv-*), que
  es donde vive este bloque. Antes había valores a mano (#5e71e4, que además ni siquiera
  era el azul del sistema, #5e72e4) y eso es como se va acumulando la deriva.

  Sin tarjeta propia: el padre ya envuelve esto en .rsv-card.
*/
.grp { display: block; }

.grp-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--rsv-line, #ecedf2);
}
.grp-head__title { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
.grp-head__text { min-width: 0; }
.grp-icon {
  width: 34px; height: 34px; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--rsv-purple-50, #f0eafd); color: var(--rsv-purple, #673de6); font-size: 15px; flex-shrink: 0;
}
.grp-h3 { font-size: 15px; font-weight: 700; color: var(--rsv-ink, #0e1320); margin: 0; letter-spacing: -0.01em; }
.grp-sub { font-size: 12.5px; color: var(--rsv-ink-3, #6b7388); margin: 2px 0 0; }
.grp-total { text-align: right; flex-shrink: 0; }
.grp-total__label { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: var(--rsv-ink-4, #9aa2b5); }
/* El importe en tinta, no en color de marca: aquí el color significa «tarifa», y el
   total es un dato. Igual que la tarjeta de «Importe y pago» de al lado. */
.grp-total__value { font-size: 20px; font-weight: 800; color: var(--rsv-ink, #0e1320); font-variant-numeric: tabular-nums; }

.grp-list { list-style: none; margin: 0; padding: 0; }
.grp-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: start;
  gap: 10px 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--rsv-line-2, #f3f4f8);
  transition: background .18s ease;
}
.grp-line:last-child { border-bottom: 0; padding-bottom: 2px; }
/* La línea que se está viendo: estado de selección, no decoración. */
.grp-line.is-current {
  background: var(--rsv-purple-50, #f0eafd);
  border-radius: 12px;
  padding: 14px 12px;
  border-bottom-color: transparent;
}
/* Sobre el tinte, el texto secundario sube de tono: gris claro sobre color no se lee. */
.grp-line.is-current .grp-line__meta,
.grp-line.is-current .grp-rooms__label { color: var(--rsv-ink-2, #3a4156); }

.grp-line__name { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.grp-line__unit { font-weight: 700; color: var(--rsv-ink, #0e1320); font-size: 14px; }
.grp-badge-current {
  font-size: 10px; font-weight: 700; color: var(--rsv-purple-700, #5028c7);
  background: #fff; box-shadow: inset 0 0 0 1px var(--rsv-purple-100, #e1d6fb);
  padding: 2px 7px; border-radius: 999px;
}

.grp-line__meta { font-size: 12px; color: var(--rsv-ink-3, #6b7388); margin-top: 4px; display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.grp-dot { opacity: .45; }

/* Tarifa. Dos significados, dos colores: propia (morado de marca) y vendida por un
   canal (azul). Ambos con contraste de sobra sobre su fondo. */
.grp-rate {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11.5px; font-weight: 700; border-radius: 999px; padding: 2px 9px;
  color: var(--rsv-purple-700, #5028c7); background: var(--rsv-purple-50, #f0eafd);
}
.grp-rate--ota {
  color: var(--rsv-blue-700, #4054c8); background: var(--rsv-blue-50, #eef0fc);
  box-shadow: inset 0 0 0 1px var(--rsv-blue-100, #dee2f9);
}
.grp-rate__icon { font-size: 9px; }

/* Habitaciones físicas: una ficha por habitación, con cifras tabulares, para leerlas
   de una en una («24 – Habitación Doble») y no como una frase corrida. */
.grp-rooms { margin-top: 7px; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.grp-rooms__icon { font-size: 10.5px; color: var(--rsv-purple, #673de6); margin-right: 1px; }
/* Ficha de habitación: número + nombre. El número manda visualmente. */
.grp-room {
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; box-shadow: inset 0 0 0 1px var(--rsv-line, #ecedf2);
  border-radius: 8px; padding: 2px 8px 2px 6px;
}
/* El NÚMERO de habitación: lo más oscuro de la línea, porque es el identificador real. */
.grp-room__code {
  font-size: 12px; font-weight: 800; color: var(--rsv-ink, #0e1320);
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
  background: var(--rsv-line-2, #f3f4f8); border-radius: 5px; padding: 0 5px; min-width: 20px; text-align: center;
}
/* El nombre acompaña, en gris, sin competir. */
.grp-room__name { font-size: 11.5px; font-weight: 600; color: var(--rsv-ink-3, #6b7388); }
/* Cuando no hay número, el nombre ES el identificador: se pinta como tal. */
.grp-room--solo .grp-room__code { background: transparent; padding: 0; }
.grp-rooms--none { margin: 7px 0 0; display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; color: var(--rsv-ink-4, #9aa2b5); }

.grp-line__qty { display: inline-flex; align-items: center; gap: 6px; padding-top: 1px; }
.grp-step {
  width: 28px; height: 28px; border-radius: 8px; border: 1px solid var(--rsv-line, #ecedf2);
  background: #fff; color: var(--rsv-ink-2, #3a4156); font-size: 16px; font-weight: 700; line-height: 1; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.grp-step:hover:not(:disabled) { border-color: var(--rsv-purple, #673de6); color: var(--rsv-purple, #673de6); }
.grp-step:active:not(:disabled) { background: var(--rsv-purple-50, #f0eafd); }
.grp-step:disabled { opacity: .45; cursor: not-allowed; }
.grp-step__n { min-width: 20px; text-align: center; font-weight: 700; color: var(--rsv-ink, #0e1320); font-variant-numeric: tabular-nums; }

.grp-line__price { font-weight: 700; color: var(--rsv-ink, #0e1320); font-size: 14px; white-space: nowrap; min-width: 74px; text-align: right; font-variant-numeric: tabular-nums; padding-top: 2px; }

.grp-line__actions { display: inline-flex; align-items: center; gap: 6px; padding-top: 1px; }
.grp-remove {
  width: 30px; height: 30px; border-radius: 8px; border: 1px solid #f4dada;
  background: #fff; color: #d92d20; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s ease, border-color .15s ease;
}
.grp-remove:hover:not(:disabled) { background: #fef3f2; border-color: #f0c2c0; }
.grp-remove:disabled { opacity: .4; cursor: not-allowed; }
.grp-confirm-txt { font-size: 12px; color: var(--rsv-ink-3, #6b7388); }
.grp-mini { border: 1px solid var(--rsv-line, #ecedf2); background: #fff; border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 700; color: var(--rsv-ink-2, #3a4156); cursor: pointer; }
.grp-mini--danger { border-color: #f0c2c0; color: #d92d20; }

/* Faltaba por completo: sin foco visible estos tres controles no se pueden usar con
   teclado, y el registro de producto pide los siete estados. */
.grp-step:focus-visible,
.grp-remove:focus-visible,
.grp-mini:focus-visible {
  outline: 2px solid var(--rsv-purple, #673de6);
  outline-offset: 2px;
}

.grp-error { margin: 12px 0 0; font-size: 12.5px; color: #b42318; background: #fef3f2; border: 1px solid #fecdca; padding: 8px 10px; border-radius: 8px; }
.grp-busy { margin-top: 10px; font-size: 12.5px; color: var(--rsv-ink-3, #6b7388); }

@media (max-width: 640px) {
  .grp-line { grid-template-columns: minmax(0, 1fr) auto; row-gap: 10px; }
  .grp-line__info { grid-column: 1 / -1; }
  .grp-line__qty { grid-column: 1; }
  .grp-line__price { grid-column: 2; text-align: right; }
  .grp-line__actions { grid-column: 2; justify-content: flex-end; }
}

@media (prefers-reduced-motion: reduce) {
  .grp-line, .grp-step, .grp-remove { transition: none; }
}
</style>
